import { useMixpanel } from '@context/MixpanelProvider';
import useWorkspacePermissions from '@hooks/workspacePermissions';
import { alpha, Box, Button, Tooltip, Typography, useTheme } from '@mui/material';
import { DASHBOARD_EVENTS } from '@utils/eventSchema';
import { ClipboardList, Mic, Plus, Upload, UploadCloud, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useWorkspace from '../../hooks/useWorkspace';

const actionsList = [
  {
    id: 1,
    title: 'Survey',
    buttonText: 'Create Survey',
    icon: ClipboardList,
    link: '/dynamic-survey/create',
    event: DASHBOARD_EVENTS.DYNAMIC_SURVEY_CREATION_START,
    color: '#10B981', // emerald
    description: 'Build dynamic surveys with branching logic and AI-powered analysis',
  },
  {
    id: 2,
    title: 'Survey Data',
    buttonText: 'Upload Survey Data',
    icon: UploadCloud,
    link: '/dynamic-survey/upload',
    event: DASHBOARD_EVENTS.DYNAMIC_SURVEY_UPLOAD_START,
    color: '#059669', // emerald dark
    description: 'Upload your dataset and let Qualz analyze survey responses',
  },
  {
    id: 3,
    title: 'Interview',
    buttonText: 'Create Interview',
    icon: Mic,
    link: '/interview/create#interview-setup',
    event: DASHBOARD_EVENTS.INTERVIEW_CREATION_START,
    color: '#3B82F6', // blue
    description: 'Conduct AI-moderated voice interviews at scale with real-time transcription',
  },
  {
    id: 4,
    title: 'Interview Data',
    buttonText: 'Upload Interview Data',
    icon: Upload,
    link: '/interview/upload',
    event: DASHBOARD_EVENTS.INTERVIEW_UPLOAD_START,
    color: '#2563EB', // blue dark
    description: 'Upload existing interviews for transcription and analysis',
  },
  {
    id: 5,
    title: 'AI Participants',
    buttonText: 'Create AI Participants',
    icon: UserPlus,
    link: '/ai-participants/generate',
    event: DASHBOARD_EVENTS.AI_PARTICIPANT_CREATION_START,
    color: '#8B5CF6', // purple
    description: 'Generate synthetic participants to test and pilot your research instruments',
  },
];

export default function QuickActions() {
  const theme = useTheme();
  const { trackEvent } = useMixpanel();
  const { activeWorkspaceId } = useWorkspace();
  const { canEdit } = useWorkspacePermissions(activeWorkspaceId);
  const navigate = useNavigate();

  const handleNavigation = (path, eventType) => {
    trackEvent('dashboard', eventType);
    navigate(path);
  };

  if (!canEdit) return null;

  return (
    <>
      <Box px={{ xs: 2, md: 3 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <Plus size={16} color={theme.palette.primary.main} />
          <Typography variant="subtitle2">Quick Actions</Typography>
        </Box>

        <Box display="flex" gap={1.5} flexWrap="wrap" data-tour="quick-actions">
          {actionsList.map((item) => {
            const Icon = item.icon;
            return (
              <Tooltip key={item.id} title={item.description} arrow placement="bottom">
                <Button
                  variant="outlined"
                  onClick={() => handleNavigation(item.link, item.event)}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    borderColor: alpha(item.color, 0.3),
                    backgroundColor: alpha(item.color, 0.04),
                    color: 'text.primary',
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: item.color,
                      backgroundColor: alpha(item.color, 0.12),
                      transform: 'translateY(-2px)',
                      boxShadow: `0 4px 16px ${alpha(item.color, 0.2)}`,
                      color: 'text.primary',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      backgroundColor: alpha(item.color, 0.12),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon size={14} color={item.color} />
                  </Box>
                  <Typography variant="body2" fontWeight={500}>
                    {item.buttonText || `Create ${item.title}`}
                  </Typography>
                </Button>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </>
  );
}
